import React, { useState } from 'react'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import PageHero from '../components/PageHero'

export default function Contact() {
  const apiBase = process.env.REACT_APP_API_BASE || ''
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' })
  const [status, setStatus] = useState('idle')
  const [alert, setAlert] = useState(null)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (status === 'loading') return
    setStatus('loading')
    setAlert(null)

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)

    try {
      const base = apiBase ? apiBase.replace(/\/$/, '') : ''
      const response = await fetch(`${base}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
        signal: controller.signal
      })

      const data = await response.json()
      if (!response.ok || !data.success) {
        throw new Error(data?.errors?.[0]?.msg || data?.error || 'Failed to send message')
      }

      setStatus('success')
      setAlert({ type: 'success', message: 'Message sent successfully! We\'ll get back to you soon.' })
      setFormData({ name: '', email: '', subject: '', message: '' })
    } catch (err) {
      setStatus('error')
      const message = err.name === 'AbortError'
        ? 'Server timed out. Please try again later.'
        : err.message || 'Failed to send message.'
      setAlert({ type: 'danger', message })
    } finally {
      clearTimeout(timeoutId)
    }
  }

  return (
    <>
      <Navbar />
      <PageHero
        title="Contact SISDI"
        subtitle="We'd love to hear from you. Reach out and let's make an impact together."
        variant="contact"
        fadeClass="fade-in"
      />

      <main>
        {/* Contact type cards */}
        <section className="section bg-light fade-in">
          <div className="container">
            <div className="grid-3">
              {[
                { icon: 'bi-envelope-fill',  title: 'General Enquiry',    desc: 'Questions about SISDI or our programs.' },
                { icon: 'bi-building',       title: 'Partnership Enquiry', desc: 'Organisations interested in collaborating.' },
                { icon: 'bi-trophy-fill',    title: 'Sponsorship Enquiry', desc: 'Brands and supporters exploring sponsorship opportunities.' },
              ].map((item, i) => (
                <div className="involve-card" key={i} style={{ textAlign: 'center', alignItems: 'center' }}>
                  <div className="icon-circle">
                    <i className={`bi ${item.icon}`} style={{ fontSize: '1.25rem', color: 'var(--green-700)' }}></i>
                  </div>
                  <h5>{item.title}</h5>
                  <p>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact form + details */}
        <section className="section fade-in">
          <div className="container">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4rem', alignItems: 'start' }}>

              {/* Details */}
              <div>
                <span className="eyebrow">Reach Out</span>
                <h2 style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
                  <span className="text-accent">Get in Touch</span>
                </h2>
                <p style={{ marginBottom: '2rem' }}>
                  Whether you have a question, want to partner with us, or are ready to volunteer,
                  send us a message and we&apos;ll get back to you!
                </p>

                <div className="contact-details">
                  <p>
                    <i className="bi bi-envelope-fill me-2 contact-icon"></i>
                    <strong>Email:</strong>{' '}
                    <a className="ntd" href="mailto:schoolsinsoccer@gmail.com">schoolsinsoccer@gmail.com</a>
                  </p>
                  <p style={{ marginTop: '0.75rem' }}>
                    <i className="bi bi-telephone-fill me-2 contact-icon"></i>
                    <strong>Phone:</strong>{' '}
                    <a className="ntd" href="tel:+2349122328676">+2349122328676</a>
                  </p>
                  <p style={{ marginTop: '0.75rem' }}>
                    <i className="bi bi-whatsapp me-2 contact-icon"></i>
                    <strong>WhatsApp:</strong>{' '}
                    <a className="ntd" href="https://wa.me/2348054619954" target="_blank" rel="noreferrer">Chat with us on WhatsApp</a>
                  </p>
                </div>

                <div className="contact-socials" style={{ marginTop: '2rem' }}>
                  <h5 style={{ marginBottom: '1rem', fontFamily: 'var(--font-heading)', color: 'var(--color-dark)' }}>
                    Follow Us
                  </h5>
                  <div className="social-icons">
                    <a href="https://web.facebook.com/profile.php?id=61563594750242" aria-label="Facebook" target="_blank" rel="noreferrer">
                      <i className="bi bi-facebook"></i>
                    </a>
                    <a href="https://x.com/schoolsinsoccer?s=20" aria-label="Twitter" target="_blank" rel="noreferrer">
                      <i className="bi bi-twitter-x"></i>
                    </a>
                    <a href="https://www.instagram.com/schoolsinsoccer_" aria-label="Instagram" target="_blank" rel="noreferrer">
                      <i className="bi bi-instagram"></i>
                    </a>
                    <a href="https://www.linkedin.com/company/schools-in-soccer-development-initiative" aria-label="LinkedIn" target="_blank" rel="noreferrer">
                      <i className="bi bi-linkedin"></i>
                    </a>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div>
                <span className="eyebrow">Send a Message</span>
                <h2 style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
                  <span className="text-accent">Send Us a Message</span>
                </h2>

                {alert && (
                  <div className={`alert alert-${alert.type}`}>{alert.message}</div>
                )}

                <form className="contact-form" onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label htmlFor="name">Full Name</label>
                      <input
                        type="text"
                        id="name"
                        className="form-control"
                        placeholder="Your full name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <label htmlFor="email">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        className="form-control"
                        placeholder="your@email.com"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="subject">Subject</label>
                    <input
                      type="text"
                      id="subject"
                      className="form-control"
                      placeholder="What is this about?"
                      name="subject"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                    />
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      className="form-control"
                      name="message"
                      rows="5"
                      placeholder="Tell us how we can help..."
                      required
                      value={formData.message}
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary btn-lg"
                    id="submitBtn"
                    disabled={status === 'loading'}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    {status === 'loading'
                      ? 'Sending...'
                      : status === 'success'
                        ? <><i className="bi bi-check-circle-fill"></i> Message Sent</>
                        : <><i className="bi bi-send-fill"></i> Send Message</>}
                  </button>
                </form>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}
